/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Component imports
import { Button, Card, Col, Form, Input, Modal, Radio, Row, Select, Tooltip, Typography } from 'antd';
import { ExclamationCircleOutlined, CopyOutlined } from '@ant-design/icons';
import VariableSelectItem from '../../../UI/VariableSelectItem';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';

// Misc imports
import { SWITCH_QUERY_CONDITIONS } from '../../../../constants/flowGraph';
import { VARIABLE_TYPES } from '../../../../constants/main';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { isArray } from '../../../../utils/is/isArray';
import { serializeLIQE, deserializeLIQE } from '../../../../utils/transform/liqe';
import { isUndefined } from '../../../../utils/is/isUndefined';
import { copyTextToClipboard } from '../../../../utils/function/copyToClipboard';
import groupBy from '../../../../utils/array/groupBy';

// Constant declarations
const { Option } = Select;
const { confirm } = Modal;
const EXPRESSION_TYPES = {
  AUTOMATED: 'automated',
  CUSTOM: 'custom'
};
const DEFAULT_CATEGORY = 'Other';

const NlpFlowQueryNodeForm = ({ form }) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { data: storyVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });
  const [selectedExpression, setSelectedExpression] = useState();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [initialValueInCurrentLang, setInitialValueInCurrentLang] = useState();
  const [localVariables, setLocalVariables] = useState([]);

  useEffect(() => {
    const switchQueryInCurrentLang = form.getFieldValue(['data', 'query']);
    setInitialValueInCurrentLang(switchQueryInCurrentLang);
    setSelectedExpression(isArray(switchQueryInCurrentLang) || isUndefined(switchQueryInCurrentLang) ? EXPRESSION_TYPES.AUTOMATED : EXPRESSION_TYPES.CUSTOM);
  }, []);

  useEffect(() => {
    const validVariables = [...storyVariables.filter((variable) => [VARIABLE_TYPES.CALLBACK, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.SESSION, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.CSAT].includes(variable.type))];
    setLocalVariables(validVariables);
  }, [storyVariables]);

  const showConfirm = (action) => {
    confirm({
      title: 'Unsaved Changes',
      icon: <ExclamationCircleOutlined />,
      content: 'Unsaved changes will be lost. Are you sure you want to continue?',
      onOk() {
        action();
      }
    });
  };

  /**
   * Used
   * @param {*} event
   */
  const changeExpression = (event) => {
    if (isArray(initialValueInCurrentLang)) {
      if (event.target.value === EXPRESSION_TYPES.AUTOMATED) {
        form.setFieldValue(['data', 'query'], initialValueInCurrentLang);
      } else {
        form.setFieldValue(['data', 'query'], deserializeLIQE(initialValueInCurrentLang) || '');
      }
    } else {
      if (event.target.value === EXPRESSION_TYPES.AUTOMATED) {
        form.setFieldValue(['data', 'query'], form.setFieldValue(['data', 'query'], [{ list: [{}] }]));
      } else {
        form.setFieldValue(['data', 'query'], initialValueInCurrentLang || '');
      }
    }
    setSelectedExpression(event.target.value);
  };

  /**
   * Triggered when the expression type is changed between Automated and Custom
   * @param {*} event The event object
   */
  const onExpressionTypeChange = (event) => {
    // Show a confirmation prompt if there are pending changes
    if (form.getFieldValue(['data', 'query']) !== initialValueInCurrentLang && form.getFieldValue(['data', 'query']) !== '') {
      showConfirm(() => changeExpression(event));
    } else {
      changeExpression(event);
    }
  };

  const updateVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setLocalVariables([...new Set([...storyVariables.filter((variable) => [VARIABLE_TYPES.CALLBACK, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.SESSION, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.CSAT].includes(variable.type)), { name: newVariableName, type: '' }])]);
    }
  };

  const renderAutomatedExpressionForm = () => {
    return (
      <Form.List
        name={['data', 'query']}
        className="mb-2"
        initialValue={[{ list: [{}] }]}
      >
        {(fields, { add, remove }, { errors }) => (
          <div className="px-6" >
            {
              fields.map(({ key, name, fieldKey, ...restField }, index) => (
                <Fragment>
                  {
                    index !== 0 ? (
                      <Form.Item
                        colon={false}
                        labelAlign="left"
                        labelCol={{ xs: 24 }}
                        noStyle
                        name={[name, 'operator']}
                        rules={[
                          {
                            required: true,
                            message: t('Select a logical operator')
                          }
                        ]}
                      >
                        <Radio.Group className="my-3">
                          <Radio.Button value="AND">{t('AND')}</Radio.Button>
                          <Radio.Button value="OR">{t('OR')}</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                    ) : (
                      <Form.Item className="mb-0">
                        <Row justify={'space-between'} align={'middle'}>
                          <Col>
                            <Typography.Text className="text-primary">
                              {t('if')}
                            </Typography.Text>
                          </Col>
                          <Col>
                            <Button onClick={() => setIsPreviewOpen(!isPreviewOpen)} type="link" className="text-primary mr-5">
                              {t('View Expression')}
                            </Button>
                          </Col>

                          {
                            isPreviewOpen ? (
                              <Col className="mb-3 pr-2 w-100 mr-7">
                                <Card>
                                  <Row justify={'space-between'} align={'middle'}>
                                    <Col span={23}>
                                      {serializeLIQE(form.getFieldValue(['data', 'query']))}
                                    </Col>

                                    <Col span={1}>
                                      <Button
                                        type="link"
                                        icon={<CopyOutlined />}
                                        onClick={() => {
                                          copyTextToClipboard(serializeLIQE(form.getFieldValue(['data', 'query'])));
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                </Card>
                              </Col>
                            ) : null
                          }
                        </Row>
                      </Form.Item>
                    )
                  }

                  <Row gutter={[4, 4]} align="middle" wrap={false}>
                    <Col flex="auto">
                      <Card
                        className="bg-layout shadow-none"
                        bordered={false}
                        size="small"
                      >
                        <Form.Item noStyle>
                          <Form.List name={[name, 'list']}>
                            {(dropdownFields, { add: addDropdownOption, remove: removeDropdownOptions }) => (
                              <Fragment>
                                {
                                  dropdownFields.map(({ key: dropdownKey, name: dropdownName, ...restDropdownField }, index) => (
                                    <Form.Item key={dropdownKey} className="mx-2 mb-0">
                                      {
                                        index !== 0 ? (
                                          <Form.Item
                                            colon={false}
                                            labelAlign="left"
                                            labelCol={{ xs: 24 }}
                                            noStyle
                                            {...restDropdownField}
                                            name={[dropdownName, 'operator']}
                                            rules={[
                                              {
                                                required: true,
                                                message: t('Select a logical operator')
                                              }
                                            ]}
                                          >
                                            <Radio.Group className="my-2">
                                              <Radio.Button value="AND">{t('AND')}</Radio.Button>
                                              <Radio.Button value="OR">{t('OR')}</Radio.Button>
                                            </Radio.Group>
                                          </Form.Item>
                                        ) : null
                                      }

                                      <Row gutter={12} wrap={false} align="middle">
                                        <Col flex="auto">
                                          <Row gutter={12} wrap={false} className="bordered border-radius-base my-2 p-1">
                                            <Col flex="auto" className="mt-2" span={10}>
                                              <Form.Item
                                                colon={false}
                                                labelAlign="left"
                                                labelCol={{ xs: 24 }}
                                                label="Variable"
                                                {...restDropdownField}
                                                name={[dropdownName, 'variable']}
                                                rules={[
                                                  {
                                                    required: true,
                                                    message: t('Enter a variable')
                                                  }
                                                ]}
                                              >
                                                <Select
                                                  showSearch
                                                  allowClear
                                                  placeholder={t('Select a variable')}
                                                  dropdownMatchSelectWidth={false}
                                                  // When the user types a new variable name, add it to the list of local variables
                                                  onSearch={updateVariables}
                                                  onBlur={(e) => updateVariables(e.target.value)}
                                                  filterOption={(input, option) => {
                                                  // Filter Option iterates through the options, find the current option to get the name and the description data
                                                    const currentVariable = localVariables.find((variable) => variable.name === option.key);
                                                    return currentVariable?.name.toLowerCase().includes(input.toLowerCase()) || currentVariable?.description?.toLowerCase().includes(input.toLowerCase());
                                                  }}
                                                  optionLabelProp="label"
                                                >
                                                  {
                                                    Object.entries(groupBy(localVariables?.filter((item, index, self) => self.findIndex(t => t.name === item.name) === index),
                                                      item => item.type || DEFAULT_CATEGORY))
                                                      .map(([groupName, items]) => (
                                                        <Select.OptGroup key={groupName} label={t(groupName || DEFAULT_CATEGORY, { context: 'variable type' })}>
                                                          {
                                                            items.map(item => (
                                                              <Select.Option
                                                                key={item.name}
                                                                value={groupName === VARIABLE_TYPES.CALLBACK ? `${item.name}.ADD_PROPERTY_HERE` : item.name}
                                                                name={item.name}
                                                                label={item.name}
                                                              >
                                                                <VariableSelectItem variable={item} showDescription={false} />
                                                              </Select.Option>
                                                            ))
                                                          }
                                                        </Select.OptGroup>
                                                      ))
                                                  }
                                                </Select>
                                              </Form.Item>
                                            </Col>

                                            <Col flex="auto" className="mt-2" span={8}>
                                              <Form.Item
                                                label={t('Condition')}
                                                name={[dropdownName, 'condition']}
                                                className="mb-2"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message: t('Select a condition')
                                                  }
                                                ]}
                                              >
                                                <Select
                                                  placeholder={t('Select a condition')}
                                                  dropdownMatchSelectWidth={false}
                                                  onChange={(e) => {
                                                    if (e === SWITCH_QUERY_CONDITIONS.NULL) {
                                                      form.setFieldValue(['data', 'query', name, 'list', dropdownName, 'value'], null);
                                                    }
                                                  }}
                                                >
                                                  {
                                                    Object.entries(SWITCH_QUERY_CONDITIONS).map(([key, value]) => (
                                                      <Option key={key} value={value}>{t(key, { context: 'switch case condition' })}</Option>
                                                    ))
                                                  }
                                                </Select>
                                              </Form.Item>
                                            </Col>

                                            <Col flex="auto" className="mt-2" span={6}>
                                              <Form.Item
                                                colon={false}
                                                labelAlign="left"
                                                labelCol={{ xs: 24 }}
                                                label="Value"
                                                name={[dropdownName, 'value']}
                                                rules={[
                                                  {
                                                    required: form.getFieldValue(['data', 'query', name, 'list', dropdownName, 'condition']) !== SWITCH_QUERY_CONDITIONS.NULL,
                                                    message: t('Enter a value')
                                                  }
                                                ]}
                                                {...restDropdownField}
                                              >
                                                <Input placeholder={t('Value')} disabled={form.getFieldValue(['data', 'query', name, 'list', dropdownName, 'condition']) === SWITCH_QUERY_CONDITIONS.NULL} />
                                              </Form.Item>
                                            </Col>
                                          </Row>
                                        </Col>

                                        <Col flex="none">
                                          <Button
                                            shape="circle"
                                            type="text"
                                            className="text-secondary"
                                            icon={<DeleteIcon />}
                                            onClick={() => removeDropdownOptions(dropdownName)}
                                          />
                                        </Col>
                                      </Row>
                                    </Form.Item>
                                  ))
                                }

                                <Button
                                  type="dashed"
                                  className="my-2"
                                  onClick={() => addDropdownOption()}
                                  icon={<PlusIcon />}
                                >
                                  {t('Add condition')}
                                </Button>
                              </Fragment>
                            )}
                          </Form.List>
                        </Form.Item>
                      </Card>
                    </Col>

                    <Col flex="none">
                      <Button
                        shape="circle"
                        type="text"
                        className="text-secondary"
                        icon={<DeleteIcon />}
                        onClick={() => fields.length !== 0 ? remove(name) : form.setFieldValue(['data', 'query'], [ { list: [{}] } ])}
                      />
                    </Col>
                  </Row>
                </Fragment>
              ))
            }

            <Button
              type="dashed"
              onClick={() => add()}
              icon={<PlusIcon />}
              className="mt-2"
            >
              {t('Add condition')}
            </Button>
          </div>
        )}
      </Form.List>
    );
  };

  return (
    <Fragment>
      <Radio.Group onChange={onExpressionTypeChange} value={selectedExpression} className="w-100">
        <Row direction="vertical">
          <Col span={24}>
            <Radio value={EXPRESSION_TYPES.AUTOMATED} className="mt-3">{t('Use Automated Expression')}</Radio>
            {selectedExpression === EXPRESSION_TYPES.AUTOMATED ? renderAutomatedExpressionForm() : null}
          </Col>

          <Col span={24}>
            <Radio value={EXPRESSION_TYPES.CUSTOM} className="mt-3">
              {t('Use Custom Expression')}

              <Tooltip
                placement="top"
                className="mt-1"
                title={
                  <Fragment>
                    <Typography.Text strong>{t('Syntax Options: ')}</Typography.Text>
                    <Trans t={t}>For more information visit <a href="https://github.com/gajus/liqe" target="_blank" rel="noreferrer">here</a></Trans>
                  </Fragment>
                }
              >
                <ExclamationCircleOutlined className="ml-2" />
              </Tooltip>
            </Radio>

            {
              selectedExpression === EXPRESSION_TYPES.CUSTOM ? (
                <Form.Item
                  name={['data', 'query']}
                  className="my-2 px-6"
                  initialValue={serializeLIQE(form.getFieldValue(['data', 'query']))}
                >
                  <Input />
                </Form.Item>
              ) : null
            }
          </Col>
        </Row>
      </Radio.Group>
    </Fragment>
  );
};

NlpFlowQueryNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default NlpFlowQueryNodeForm;
